import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Input } from './Input';

/**
 * Unified Select Component
 *
 * This component combines the functionality of both Select and SearchableSelect.
 * Use the `searchable` prop to enable search/filter functionality.
 *
 * @example
 * // Basic Select
 * <Select value={value} onValueChange={setValue}>
 *   <SelectTrigger>
 *     <SelectValue placeholder="Select an option" />
 *   </SelectTrigger>
 *   <SelectContent>
 *     <SelectItem value="option1">Option 1</SelectItem>
 *     <SelectItem value="option2">Option 2</SelectItem>
 *   </SelectContent>
 * </Select>
 *
 * @example
 * // Searchable Select
 * <Select value={value} onValueChange={setValue} searchable>
 *   <SelectTrigger>
 *     <SelectValue placeholder="Search and select" />
 *   </SelectTrigger>
 *   <SelectContent>
 *     <SelectItem value="option1" searchText="Option One">Option 1</SelectItem>
 *     <SelectItem value="option2" searchText="Option Two">Option 2</SelectItem>
 *   </SelectContent>
 * </Select>
 */

interface SelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
  /** Enable search/filter functionality */
  searchable?: boolean;
  /** Placeholder text for the search input (when searchable is true) */
  searchPlaceholder?: string;
  /** Default label to show when value is pre-populated (useful for edit modes) */
  defaultLabel?: string;
}

interface SelectTriggerProps {
  className?: string;
  children: React.ReactNode;
}

interface SelectContentProps {
  className?: string;
  children: React.ReactNode;
}

interface SelectItemProps {
  value: string;
  className?: string;
  children: React.ReactNode;
  /** Optional text used for filtering in searchable mode */
  searchText?: string;
}

interface SelectValueProps {
  placeholder?: string;
  className?: string;
}

// Context for sharing state between components
interface SelectContextValue {
  value?: string;
  onValueChange?: (value: string) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  placeholder?: string;
  searchable: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedLabel: string;
  setSelectedLabel: (label: string) => void;
  defaultLabel?: string;
}

const SelectContext = React.createContext<SelectContextValue>({
  isOpen: false,
  setIsOpen: () => {},
  searchable: false,
  searchQuery: '',
  setSearchQuery: () => {},
  selectedLabel: '',
  setSelectedLabel: () => {},
});

export function Select({
  value,
  onValueChange,
  placeholder,
  disabled: _disabled,
  className,
  children,
  searchable = false,
  searchPlaceholder: _searchPlaceholder = "Search...",
  defaultLabel,
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (searchable) {
          setSearchQuery('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchable]);

  const contextValue: SelectContextValue = {
    value,
    onValueChange,
    isOpen,
    setIsOpen: (open: boolean) => {
      setIsOpen(open);
      if (!open && searchable) {
        setSearchQuery('');
      }
    },
    placeholder,
    searchable,
    searchQuery,
    setSearchQuery,
    selectedLabel,
    setSelectedLabel,
    defaultLabel,
  };

  return (
    <SelectContext.Provider value={contextValue}>
      <div ref={selectRef} className={cn('relative', className)}>
        {children}
      </div>
    </SelectContext.Provider>
  );
}

export const SelectTrigger = forwardRef<HTMLButtonElement, SelectTriggerProps>(
  ({ className, children, ...props }, ref) => {
    const { isOpen, setIsOpen } = React.useContext(SelectContext);

    return (
      <button
        ref={ref}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center justify-between w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm',
          'hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          className
        )}
        {...props}
      >
        {children}
        <ChevronDown className={cn(
          'w-4 h-4 text-gray-400 transition-transform duration-200',
          isOpen && 'transform rotate-180'
        )} />
      </button>
    );
  }
);

SelectTrigger.displayName = 'SelectTrigger';

export function SelectContent({ className, children }: SelectContentProps) {
  const { isOpen, searchable, searchQuery, setSearchQuery } = React.useContext(SelectContext);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      // Focus search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen, searchable]);

  if (!isOpen) return null;

  return (
    <div className={cn(
      'absolute z-50 w-full mt-1 border rounded-md shadow-lg overflow-hidden',
      'bg-[rgb(var(--brand-surface-primary))] border-[rgb(var(--brand-border-primary))]',
      searchable ? 'max-h-80' : 'max-h-60',
      className
    )}>
      {/* Search input (only for searchable mode) */}
      {searchable && (
        <div className="p-2 border-b border-[rgb(var(--brand-border-primary))] bg-[rgb(var(--brand-surface-primary))]">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 icon-md text-[rgb(var(--brand-text-tertiary))]" />
            <Input
              ref={searchInputRef}
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-sm"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
      <div className={cn('py-1 bg-[rgb(var(--brand-surface-primary))]', searchable && 'max-h-60 overflow-auto')}>
        {children}
      </div>
    </div>
  );
}

export function SelectItem({ value, className, children, searchText }: SelectItemProps) {
  const {
    value: selectedValue,
    onValueChange,
    setIsOpen,
    searchable,
    searchQuery,
    setSelectedLabel
  } = React.useContext(SelectContext);

  const handleClick = () => {
    onValueChange?.(value);
    setSelectedLabel(children?.toString() || '');
    setIsOpen(false);
  };

  const isSelected = selectedValue === value;

  // Filter based on search query (only in searchable mode)
  if (searchable && searchQuery) {
    const textToSearch = searchText || children?.toString() || '';
    const matchesSearch = textToSearch.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) {
      return null;
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'w-full px-3 py-2 text-left text-sm',
        'text-[rgb(var(--brand-text-primary))]',
        'hover:bg-[rgb(var(--brand-surface-secondary))]',
        'focus:bg-[rgb(var(--brand-surface-secondary))] focus:outline-none',
        isSelected && 'bg-[rgba(var(--brand-primary-500),0.1)] text-[rgb(var(--brand-primary-700))]',
        className
      )}
    >
      {children}
    </button>
  );
}

export function SelectValue({ placeholder, className }: SelectValueProps) {
  const { value, selectedLabel, placeholder: contextPlaceholder, searchable, defaultLabel } = React.useContext(SelectContext);

  // For searchable selects, use the selectedLabel (display text)
  // If no selectedLabel but value exists, use defaultLabel (for pre-populated values)
  // For regular selects, use the value
  const displayValue = searchable
    ? (selectedLabel || (value && defaultLabel) || '')
    : value;

  return (
    <span className={cn(
      displayValue ? 'text-[rgb(var(--brand-text-primary))]' : 'text-[rgb(var(--brand-text-tertiary))]',
      className
    )}>
      {displayValue || placeholder || contextPlaceholder}
    </span>
  );
}

// Hook for using Select context externally (useful for custom implementations)
export function useSelectContext() {
  const context = React.useContext(SelectContext);
  if (!context) {
    throw new Error('useSelectContext must be used within a Select component');
  }
  return context;
}
